import { Check, Loader2, X } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/client";
import { qk } from "../api/queries";
import type { PanneauKind, Proposal } from "../api/types";

interface Props {
  /** All proposals from the latest agent turn — this card filters to
   *  `panneau_create` and renders one banner row per proposal. */
  proposals: Proposal[];
  /** Conversation id, used to invalidate the panneaux list after
   *  Valider so the new panneau shows up immediately. */
  conv: string;
  /** Called once a proposal is validated or refused, so the parent can
   *  drop it from `lastProposals` and the banner stops re-rendering. */
  onResolved: (proposalId: string) => void;
}

const KIND_LABEL: Record<PanneauKind, string> = {
  commande_sortante: "Commande sortante",
  inventaire: "Inventaire",
  rapport: "Rapport",
};

/**
 * Pending `panneau_create` proposals don't fit inside any existing
 * panneau (the panneau doesn't exist yet — that's the whole proposal).
 * We surface them as a banner above the panneau grid, mirroring the
 * pattern from `OrphanProposalsCTA` so operators see one consistent
 * "agent suggested this — Valider/Refuser" affordance.
 *
 * UI contract: the user MUST click Valider before the panneau is
 * created. PRD §C invariant.
 */
export function PendingPanneauCard({ proposals, conv, onResolved }: Props) {
  const qc = useQueryClient();

  // Per-proposal validate / reject. Panneau-id-less endpoints because
  // the panneau doesn't exist yet — the runtime's commit_proposal
  // resolves the panneau_create handler from the proposal type.
  // We point at the same per-proposal endpoint shape used elsewhere;
  // backend handler dispatches on proposal type.
  const validate = useMutation({
    mutationFn: (proposalId: string) =>
      api<{ committed: string[]; skipped: string[] }>(
        `/api/proposals/${proposalId}/validate`,
        { method: "POST" },
      ),
    onSuccess: (_data, proposalId) => {
      qc.invalidateQueries({ queryKey: qk.panneaux(conv) });
      onResolved(proposalId);
    },
  });
  const reject = useMutation({
    mutationFn: (proposalId: string) =>
      api<void>(`/api/proposals/${proposalId}/reject`, { method: "POST" }),
    onSuccess: (_data, proposalId) => {
      onResolved(proposalId);
    },
  });

  const pending = proposals.filter((p) => p.type === "panneau_create");
  if (pending.length === 0) return null;

  return (
    <div className="max-w-6xl mx-auto mt-2 mb-1 px-4">
      <div className="rounded-xl bg-white dark:bg-surface-dark ring-1 ring-dashed ring-ink/30 dark:ring-white/30 overflow-hidden border border-dashed border-ink/30 dark:border-white/30">
        <header className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-ink dark:text-ink-dark border-b border-dashed border-ink/20 dark:border-white/20 bg-ink/5">
          <span>
            {pending.length === 1
              ? "L'agent propose de créer 1 panneau"
              : `L'agent propose de créer ${pending.length} panneaux`}
          </span>
        </header>
        <ul className="divide-y divide-black/5">
          {pending.map((p) => {
            const name = String(p.payload.name ?? "");
            const kind = (p.payload.kind ?? "commande_sortante") as PanneauKind;
            const cfgEntries = Object.entries(p.payload.config ?? {});
            const isValidating =
              validate.isPending && validate.variables === p.id;
            const isRejecting =
              reject.isPending && reject.variables === p.id;
            return (
              <li key={p.id} className="px-3 py-2 flex flex-wrap items-center gap-2 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-medium italic opacity-80">
                    « {name} »
                    <span className="ml-2 text-xs text-muted dark:text-muted-dark not-italic">
                      {KIND_LABEL[kind] ?? String(kind)}
                    </span>
                  </div>
                  {cfgEntries.length > 0 && (
                    <div className="text-xs text-muted dark:text-muted-dark mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                      {cfgEntries.map(([k, v]) => (
                        <span key={k}>
                          <span className="opacity-70">{k}:</span>{" "}
                          <span>{String(v)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="text-xs text-muted dark:text-muted-dark italic mt-0.5">
                    « {p.source_quote} »
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => validate.mutate(p.id)}
                  disabled={isValidating || isRejecting}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-ink text-white text-xs font-semibold hover:bg-ink/90 disabled:opacity-50"
                  aria-label={`Valider la création du panneau ${name}`}
                >
                  {isValidating ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Check className="h-3 w-3" />
                  )}
                  Valider
                </button>
                <button
                  type="button"
                  onClick={() => reject.mutate(p.id)}
                  disabled={isValidating || isRejecting}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white dark:bg-surface-dark ring-1 ring-black/15 dark:ring-white/15 text-ink dark:text-ink-dark text-xs hover:ring-ink/40 dark:hover:ring-white/40 disabled:opacity-50"
                  aria-label={`Refuser la création du panneau ${name}`}
                >
                  {isRejecting ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <X className="h-3 w-3" />
                  )}
                  Refuser
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
